import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  AssistantActiveRouteContextEnvelope,
  RunReportsVisualizationRouteIntent,
} from "@triedge/domain-core";
import type { AssistantFeatureLocalDraftActionHandler } from "../../feature-bundle/types";
import type { AssistantPolicyEventHandler } from "../../types";
import {
  RUN_REPORTS_ASSISTANT_CONTEXT_KEY,
  RUN_REPORTS_ASSISTANT_PATHNAME,
  buildRunReportsActiveRouteContextEnvelope,
  buildRunReportsAssistantSnapshot,
  executeRunReportsAssistantDraftAction,
  executeRunReportsAssistantPolicyEvent,
  type RunReportsAssistantSnapshot,
} from "./runReportsAssistantPolicy";
import type {
  ReportGenerationAdvancedVisualizationActiveView,
  ReportGenerationAdvancedVisualizationBodySurface,
  ReportGenerationAdvancedVisualizationChartType,
  ReportGenerationSurfaceTheme,
  ReportGenerationWorkspaceModel,
} from "./types";

export type RunReportsAssistantRouteContext = {
  routeKey: string;
  pathname: string;
  context: Record<string, unknown>;
  activeRouteContext: AssistantActiveRouteContextEnvelope;
  onAssistantPolicyEvent: AssistantPolicyEventHandler;
  onLocalDraftAction?: AssistantFeatureLocalDraftActionHandler;
};

export type RunReportsRouteControllerVisualAuthority = {
  authoritySource?: string;
  referenceSource?: string;
  routeAuthoritySource?: string;
  routeReferenceSource?: string;
  extractedFrom?: string;
};

export type RunReportsRouteShellProps = {
  className: string;
  "data-testid": string;
  "data-authority-source": string;
  "data-reference-source": string;
  "data-extracted-from": string;
  "data-route-authority-source"?: string;
  "data-route-reference-source"?: string;
};

export type UseRunReportsRouteControllerOptions = {
  model: ReportGenerationWorkspaceModel;
  routeKey?: string;
  pathname?: string;
  contextNamespace?: string;
  surfaceTheme?: ReportGenerationSurfaceTheme;
  defaultTemplateId?: string;
  defaultReportTitle?: string;
  resolveTemplateTitle?: (
    template: ReportGenerationWorkspaceModel["galleryTemplates"][number],
  ) => string;
  defaultExtraFieldIds?: readonly string[];
  skipDefaultInitialization?: boolean;
  includeLocalDraftAction?: boolean;
  buildContextExtras?: (input: {
    model: ReportGenerationWorkspaceModel;
    workspaceModel: ReportGenerationWorkspaceModel;
    assistantSnapshot: RunReportsAssistantSnapshot;
    activeRouteContext: AssistantActiveRouteContextEnvelope;
  }) => Record<string, unknown>;
  onRouteContextChange?: (next: RunReportsAssistantRouteContext | null) => void;
  onUnmount?: () => void;
  visualAuthority?: RunReportsRouteControllerVisualAuthority;
  routeIntent?: RunReportsVisualizationRouteIntent | null;
};

export type RunReportsRouteController = {
  workspaceModel: ReportGenerationWorkspaceModel;
  assistantSnapshot: RunReportsAssistantSnapshot;
  activeRouteContext: AssistantActiveRouteContextEnvelope;
  assistantRouteContext: RunReportsAssistantRouteContext;
  handleAssistantPolicyEvent: AssistantPolicyEventHandler;
  handleLocalDraftAction: AssistantFeatureLocalDraftActionHandler;
  routeShellProps: RunReportsRouteShellProps;
  surfaceTheme: ReportGenerationSurfaceTheme;
};

const hasMappedVisualPolicy = (
  model: ReportGenerationWorkspaceModel,
): boolean => {
  const chartVisualization = model.advancedVisualization?.chartVisualization ?? null;
  return (chartVisualization?.chartOptions.length ?? 0) > 0;
};

export const useRunReportsRouteController = ({
  model,
  routeKey = "school-run-reports",
  pathname = RUN_REPORTS_ASSISTANT_PATHNAME,
  contextNamespace,
  surfaceTheme = "light",
  defaultTemplateId = "aging-report",
  defaultReportTitle = "Tuition report",
  resolveTemplateTitle,
  defaultExtraFieldIds = [],
  skipDefaultInitialization = false,
  includeLocalDraftAction = false,
  buildContextExtras,
  onRouteContextChange,
  onUnmount,
  visualAuthority,
  routeIntent,
}: UseRunReportsRouteControllerOptions): RunReportsRouteController => {
  const initialRouteIntentWantsVisual =
    routeIntent?.visualization.bodySurface === "visual";
  const [bodySurface, setBodySurface] =
    useState<ReportGenerationAdvancedVisualizationBodySurface>(
      initialRouteIntentWantsVisual ? "visual" : "table",
    );
  const [visualizationView, setVisualizationView] =
    useState<ReportGenerationAdvancedVisualizationActiveView>(
      routeIntent?.visualization.view ?? "chart",
    );
  const [visualizationChartType, setVisualizationChartType] =
    useState<ReportGenerationAdvancedVisualizationChartType | null>(
      routeIntent?.visualization.chartType ?? null,
    );
  const [pendingVisualActivation, setPendingVisualActivation] =
    useState(initialRouteIntentWantsVisual);
  const pendingVisualActivationRef = useRef(initialRouteIntentWantsVisual);
  const pendingRouteIntentRef = useRef(Boolean(routeIntent));
  const templateSelectionSignature =
    `${model.filterState.selectedTemplateSource ?? "none"}:${model.filterState.selectedTemplateId ?? "none"}`;
  const previousTemplateSelectionSignatureRef = useRef(templateSelectionSignature);
  const workspaceModelRef = useRef<ReportGenerationWorkspaceModel>(model);
  const initializedRef = useRef(false);
  const visualBodySurfaceAvailable = hasMappedVisualPolicy(model);
  const setRouteBodySurface = useCallback(
    (surface: ReportGenerationAdvancedVisualizationBodySurface) => {
      if (surface === "visual") {
        pendingVisualActivationRef.current = true;
        setPendingVisualActivation(true);
        setBodySurface("visual");
        return;
      }
      if (pendingVisualActivationRef.current && !visualBodySurfaceAvailable) {
        setBodySurface("table");
        return;
      }
      pendingVisualActivationRef.current = false;
      setPendingVisualActivation(false);
      setBodySurface("table");
    },
    [visualBodySurfaceAvailable],
  );
  const workspaceModel = useMemo<ReportGenerationWorkspaceModel>(() => ({
    ...model,
    bodySurface,
    visualizationView,
    visualizationChartType,
    actions: {
      ...model.actions,
      setBodySurface: setRouteBodySurface,
      setVisualizationView,
      setVisualizationChartType,
    },
  }), [
    bodySurface,
    model,
    setRouteBodySurface,
    visualizationChartType,
    visualizationView,
  ]);
  const assistantSnapshot = useMemo(
    () => buildRunReportsAssistantSnapshot(workspaceModel),
    [workspaceModel],
  );
  const assistantSnapshotKey = useMemo(
    () => JSON.stringify(assistantSnapshot),
    [assistantSnapshot],
  );
  const activeRouteContext = useMemo(
    () => buildRunReportsActiveRouteContextEnvelope(
      assistantSnapshot,
      { contextNamespace },
    ),
    [assistantSnapshotKey, contextNamespace],
  );
  const handleLocalDraftAction = useCallback<AssistantFeatureLocalDraftActionHandler>(
    (action) => executeRunReportsAssistantDraftAction({
      model: workspaceModelRef.current,
      action,
    }),
    [],
  );
  const handleAssistantPolicyEvent = useCallback<AssistantPolicyEventHandler>(
    (event) => executeRunReportsAssistantPolicyEvent({
      model: workspaceModelRef.current,
      event,
      activeRouteContext,
    }),
    [activeRouteContext],
  );
  const contextExtras = useMemo(
    () => buildContextExtras?.({
      model,
      workspaceModel,
      assistantSnapshot,
      activeRouteContext,
    }) ?? {},
    [
      activeRouteContext,
      assistantSnapshotKey,
      buildContextExtras,
      model,
      workspaceModel,
    ],
  );
  const contextExtrasKey = useMemo(
    () => JSON.stringify(contextExtras),
    [contextExtras],
  );
  const assistantRouteContext = useMemo<RunReportsAssistantRouteContext>(() => {
    const next: RunReportsAssistantRouteContext = {
      routeKey,
      pathname,
      context: {
        [RUN_REPORTS_ASSISTANT_CONTEXT_KEY]: assistantSnapshot,
        ...contextExtras,
      },
      activeRouteContext,
      onAssistantPolicyEvent: handleAssistantPolicyEvent,
    };
    if (includeLocalDraftAction) {
      next.onLocalDraftAction = handleLocalDraftAction;
    }
    return next;
  }, [
    activeRouteContext,
    assistantSnapshotKey,
    contextExtrasKey,
    handleAssistantPolicyEvent,
    handleLocalDraftAction,
    includeLocalDraftAction,
    pathname,
    routeKey,
  ]);
  const routeShellProps = useMemo<RunReportsRouteShellProps>(() => ({
    className: "h-full min-h-0 w-full overflow-hidden",
    "data-testid": "run-reports-route",
    "data-authority-source":
      visualAuthority?.authoritySource ?? "report-generation-magic-patterns",
    "data-reference-source":
      visualAuthority?.referenceSource ??
      "apps/ystnj/storybook-assets/external-ui/run-reports",
    "data-extracted-from":
      visualAuthority?.extractedFrom ??
      "package:@triedge/ai-assistant-ui/report-generation",
    ...(visualAuthority?.routeAuthoritySource
      ? { "data-route-authority-source": visualAuthority.routeAuthoritySource }
      : {}),
    ...(visualAuthority?.routeReferenceSource
      ? { "data-route-reference-source": visualAuthority.routeReferenceSource }
      : {}),
  }), [
    visualAuthority?.authoritySource,
    visualAuthority?.extractedFrom,
    visualAuthority?.referenceSource,
    visualAuthority?.routeAuthoritySource,
    visualAuthority?.routeReferenceSource,
  ]);

  useEffect(() => {
    workspaceModelRef.current = workspaceModel;
  }, [workspaceModel]);

  useEffect(() => {
    pendingVisualActivationRef.current = pendingVisualActivation;
  }, [pendingVisualActivation]);

  useEffect(() => {
    if (previousTemplateSelectionSignatureRef.current === templateSelectionSignature) {
      return;
    }
    previousTemplateSelectionSignatureRef.current = templateSelectionSignature;
    if (pendingRouteIntentRef.current) {
      return;
    }
    pendingVisualActivationRef.current = false;
    setPendingVisualActivation(false);
    setBodySurface("table");
    setVisualizationView("chart");
    setVisualizationChartType(null);
  }, [templateSelectionSignature]);

  useEffect(() => {
    if (!routeIntent) {
      pendingRouteIntentRef.current = false;
      return;
    }
    if (!visualBodySurfaceAvailable) {
      pendingVisualActivationRef.current = true;
      setPendingVisualActivation(true);
      return;
    }
    pendingRouteIntentRef.current = false;
    pendingVisualActivationRef.current = false;
    setPendingVisualActivation(false);
    setBodySurface("visual");
    setVisualizationView(routeIntent.visualization.view);
    setVisualizationChartType(routeIntent.visualization.chartType ?? null);
  }, [
    routeIntent,
    routeIntent?.visualization.chartType,
    routeIntent?.visualization.view,
    visualBodySurfaceAvailable,
  ]);

  useEffect(() => {
    if (!pendingVisualActivation || !visualBodySurfaceAvailable) {
      return;
    }
    pendingVisualActivationRef.current = false;
    setPendingVisualActivation(false);
    setBodySurface("visual");
  }, [pendingVisualActivation, visualBodySurfaceAvailable]);

  useEffect(() => () => {
    onUnmount?.();
  }, [onUnmount]);

  useEffect(() => {
    onRouteContextChange?.(assistantRouteContext);
  }, [assistantRouteContext, onRouteContextChange]);

  useEffect(() => () => {
    onRouteContextChange?.(null);
  }, [onRouteContextChange]);

  useEffect(() => {
    if (
      skipDefaultInitialization ||
      initializedRef.current ||
      model.galleryTemplates.length === 0
    ) {
      return;
    }
    initializedRef.current = true;
    model.actions.closeExport();
    model.actions.closeVersionHistory();
    model.actions.setContextMenuField(null);
    model.actions.setSelectedHistoryEntry(null);
    model.actions.openWorkspace("filters");
    model.actions.setActiveTab("filters");
    model.actions.setTemplateView("gallery");
    model.actions.setCollapsed(false);

    if (
      model.filterState.selectedTemplateId === defaultTemplateId &&
      model.filterState.selectedTemplateSource === "gallery" &&
      model.filterState.filterState.selectedEntities.length > 0 &&
      model.filterState.filterState.selectedFields.length > 0
    ) {
      const selectedTemplate = model.galleryTemplates.find(
        (template) => template.id === defaultTemplateId,
      );
      model.actions.setReportTitle(
        selectedTemplate
          ? (resolveTemplateTitle?.(selectedTemplate) ?? selectedTemplate.name)
          : defaultReportTitle,
      );
      return;
    }

    const defaultTemplate =
      model.galleryTemplates.find((template) => template.id === defaultTemplateId) ??
      model.galleryTemplates[0] ??
      null;

    if (defaultTemplate) {
      model.actions.applyTemplateById(defaultTemplate.id, "gallery");
      for (const fieldId of defaultExtraFieldIds) {
        if (!defaultTemplate.fields.includes(fieldId)) {
          model.actions.toggleField(fieldId);
        }
      }
    }
  }, [
    defaultExtraFieldIds,
    defaultReportTitle,
    defaultTemplateId,
    model.actions,
    model.galleryTemplates,
    resolveTemplateTitle,
    skipDefaultInitialization,
  ]);

  return {
    workspaceModel,
    assistantSnapshot,
    activeRouteContext,
    assistantRouteContext,
    handleAssistantPolicyEvent,
    handleLocalDraftAction,
    routeShellProps,
    surfaceTheme,
  };
};
